<template>
  <div>
    <div class="wz-page-header">
      <!-- Mismo patrón que ContractList: wz-page-header es flex-column, por eso anidamos un flex-row
           para poner el título a la izquierda y el panel de columnas a la derecha, en la misma fila. -->
      <div class="flex items-center justify-between">
        <h1 class="wz-page-title mb-0">Colaboradores</h1>
        <!-- Tooltip interno suprimido (tooltip-text="") + wz-tooltip envolvente a la izquierda.
             Botón con su variant default primary-soft (sin overrides). -->
        <wz-tooltip text="Configurar columnas" position="left">
          <wz-columns-preferences-panel
            tooltip-text=""
            :columns="columns"
            :hidden-columns="hiddenColumns"
            @toggle="toggleColumn"
            @reorder="reorderColumns"
            @reset="resetColumns"
          />
        </wz-tooltip>
      </div>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
        </template>

        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-tooltip text="Descargar">
            <wz-button variant="outline" @click="downloadReport()">
              <i class="ri-download-2-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por nombre o número de documento"
            @search="onSearch"
            @input="search = $event"
          />
          <wz-dropdown
            :items="bulkUploadOptions"
            trigger-icon="ri-upload-cloud-line"
            trigger-title="Carga masiva"
            @click="onBulkUploadAction"
          />
          <wz-button @click="$router.push({ name: 'employee-create' })">Agregar</wz-button>
        </template>

        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th
              v-for="col in visibleColumns"
              :key="col.key"
              :class="[{
                'wz-th-sortable': col.sortKey,
                'is-sorted': col.sortKey && sortKey === col.sortKey,
              }]"
              @click="col.sortKey ? toggleSort(col.sortKey) : null"
            >
              {{ col.label }}
              <i v-if="col.sortKey" :class="['wz-th-sort-icon', sortIcon(col.sortKey)]"></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="o in sortedList(list)" :key="o.id">
            <td
              v-for="col in visibleColumns"
              :key="col.key"
              :class="{ uppercase: col.key === 'company' }"
              :style="cellStyle(col.key)"
            >
              <template v-if="col.key === 'code'">{{ o.code }}</template>

              <template v-else-if="col.key === 'fullName'">
                <div class="flex items-center" style="gap: 8px;">
                  <wz-avatar :photo-url="photoUrls[o.id] || null" :name="o.fullName" size="sm" />
                  <span>{{ o.fullName || '-' }}</span>
                </div>
              </template>

              <template v-else-if="col.key === 'company'">{{ companies(o) }}</template>
              <template v-else-if="col.key === 'area'">{{ areas(o) }}</template>
              <template v-else-if="col.key === 'position'">{{ positions(o) }}</template>

              <template v-else-if="col.key === 'status'">
                <div style="display: flex; flex-direction: column; align-items: flex-start; gap: 4px;">
                  <wz-badge :variant="statusVariant(o.displayStatus)" pill>
                    {{ o.displayStatus }}
                  </wz-badge>
                  <template v-if="o.displayStatus === 'ACTIVO'">
                    <wz-badge v-if="o.activeMedicalLeaveType" variant="info-solid" pill>
                      {{ medicalLeaveLabel(o.activeMedicalLeaveType) }}
                    </wz-badge>
                    <wz-badge v-if="o.activeLeaveTypeCode" variant="info-solid" pill>
                      {{ leaveLabel(o.activeLeaveTypeCode) }}
                    </wz-badge>
                    <wz-badge v-if="o.inProbation" variant="warning-solid" pill>
                      EN PRUEBA
                    </wz-badge>
                    <!-- Red de seguridad UI: el backend ya excluye los renovados de "por vencer", pero se
                         prioriza RENOVADO por si ambos llegaran juntos (sucesor ya iniciado, p.ej.). -->
                    <wz-badge v-if="o.contractExpiringSoon && !o.hasRegisteredRenewal" variant="danger-solid" pill>
                      CONTRATO POR VENCER
                    </wz-badge>
                    <wz-badge v-if="o.hasRegisteredRenewal" variant="success-solid" pill>
                      RENOVADO
                    </wz-badge>
                    <wz-badge v-if="o.onVacation" variant="info-solid" pill>
                      EN VACACIONES
                    </wz-badge>
                    <wz-badge v-if="o.nearIndefinite" variant="warning-solid" pill>
                      PRÓXIMO A INDETERMINADO
                    </wz-badge>
                  </template>
                  <!-- Docs incompletos: independiente del estado del colaborador -->
                  <wz-badge v-if="o.hasIncompleteDocumentation" variant="warning-solid" pill>
                    DOCS INCOMPLETOS
                  </wz-badge>
                </div>
              </template>

              <template v-else-if="col.key === 'docs'">
                <wz-tooltip v-if="o.documentationComplete" text="Documentación completa">
                  <i class="ri-checkbox-circle-line text-success" style="font-size: 1.25rem;"></i>
                </wz-tooltip>
                <wz-tooltip v-else text="Faltan documentos obligatorios">
                  <i class="ri-error-warning-line text-warning" style="font-size: 1.25rem;"></i>
                </wz-tooltip>
              </template>

              <template v-else-if="col.key === 'options'">
                <wz-dropdown
                  :items="employeeActions(o)"
                  placement="left"
                  @click="handleEmployeeAction(o, $event)"
                />
              </template>
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <wz-pagination
          v-if="list.length"
          :total="total"
          :current-page="page"
          :per-page="pageSize"
          @change="onPaginationChange"
        />
        <div class="table-no-results" v-else>
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </wz-table-footer>

    </wz-table-container>

    <!-- Dar de baja: modal de cese reutilizable -->
    <cease-contract-modal v-model="ceaseModal" :contract="ceaseTarget" @ceased="onCeased" />

    <!-- Selector de empresa cuando hay varios contratos activos -->
    <wz-modal v-model="companySelectModal" title="Elegir empresa a cesar">
      <p class="mb-3 text-muted">El colaborador tiene contratos activos en varias empresas. Elige cuál cesar.</p>
      <wz-select
        v-model="selectedBajaContractId"
        :items="bajaContractOptions"
        defaultOption="Elegir empresa"
      />
      <template #footer>
        <wz-button variant="outline-secondary" @click="companySelectModal = false">Cancelar</wz-button>
        <wz-button variant="primary" :disabled="!selectedBajaContractId" @click="confirmCompanySelect">Continuar</wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { Util } from "@wizepe/wizeui"
import { FilterMixin } from "@wizepe/wizeui"
import { PaginationMixin } from "@wizepe/wizeui"
import { SortMixin } from '@wizepe/wizeui'
import { draggableColumnsMixin } from '@wizepe/wizeui'
import { EmployeeService, AreaService, PositionService, CompanyService } from '@/core/services'
import ColumnPreferencesService from '@/core/services/ColumnPreferencesService'
import { downloadErrorMessage } from '@/core/utils'
import fileDownload from 'js-file-download'
import CeaseContractModal from '@/modules/Contracts/Components/CeaseContractModal.vue'

// Orden por defecto. Todas las columnas son reordenables por drag & drop (incluida "Opciones").
function defaultColumns() {
  return [
    { key: 'code',     label: 'Código',          sortKey: 'code',             fixed: false },
    { key: 'fullName', label: 'Nombre Completo', sortKey: 'fullName',         fixed: false },
    { key: 'company',  label: 'Empresa',         sortKey: '_companyDisplay',  fixed: false },
    { key: 'area',     label: 'Área',            sortKey: '_areaDisplay',     fixed: false },
    { key: 'position', label: 'Cargo',           sortKey: '_positionDisplay', fixed: false },
    { key: 'status',   label: 'Estado',          sortKey: 'status',           fixed: false },
    { key: 'docs',     label: 'Docs',            sortKey: null,               fixed: false },
    { key: 'options',  label: 'Opciones',        sortKey: null,               fixed: true },
  ]
}

export default {
  components: { CeaseContractModal },
  mixins: [SortMixin, PaginationMixin, FilterMixin, draggableColumnsMixin],
  props: {
    // Servicio de preferencias por usuario (backend). Override del prop del mixin con default,
    // ya que esta página no recibe props de un padre.
    preferencesService: { type: Object, default: () => ColumnPreferencesService },
  },
  data() {
    return {
      list: [],
      photoUrls: {},
      // Opciones del dropdown "Carga masiva" (colaboradores + derechohabientes).
      bulkUploadOptions: [
        { key: 'employees', label: 'Carga masiva de colaboradores' },
        { key: 'family',    label: 'Carga masiva de derechohabientes' },
      ],
      columnsStorageKey: 'crystal_employee_columns_order',
      columnsListKey: 'employees',
      // Dar de baja (cese del contrato activo)
      ceaseModal: false,
      ceaseTarget: null,
      bajaEmployee: null,
      companySelectModal: false,
      bajaContracts: [],
      selectedBajaContractId: null,
      tooltipType: null,
      // Valor por defecto y opciones como String coherentes con displayStatus del colaborador.
      filters: { status: 'ACTIVO' },
      filterOptions: [
        {
          label: 'Estado',
          key: 'status',
          type: 'SINGLE',
          options: [
            { key: 'ACTIVO',       label: 'ACTIVO' },
            { key: 'VENCIDO',      label: 'VENCIDO' },
            { key: 'SIN_CONTRATO', label: 'SIN CONTRATO' },
            { key: 'CESADO',       label: 'CESADO' },
          ],
        },
        {
          label: 'Área',
          key: 'areaIds',
          type: 'MULTIPLE',
          options: [],
        },
        {
          label: 'Cargo',
          key: 'roleIds',
          type: 'MULTIPLE',
          options: [],
        },
        {
          label: 'Fecha de Ingreso',
          key: 'contractInitiation',
          type: 'DATE',
          isRange: true
        },
        {
          label: 'Fecha de Cese',
          key: 'contractFinish',
          type: 'DATE',
          isRange: true
        },
        {
          label: 'Empresa',
          key: 'companyId',
          type: 'SINGLE',
          options: [],
        }
      ],
    }
  },
  computed: {
    bajaContractOptions() {
      return (this.bajaContracts || []).map(c => ({
        key: c.id,
        label: c.companyName || `Contrato ${c.contractNumber}`,
      }))
    },
  },
  methods: {
    // Dropdown "Carga masiva": navega según la opción elegida.
    onBulkUploadAction(item) {
      const key = item && item.key ? item.key : item
      if (key === 'employees') this.$router.push({ name: 'employee-bulk-upload' })
      else if (key === 'family') this.$router.push({ name: 'family-bulk-upload' })
    },
    // Estilo por celda (mantiene el layout original de cada columna).
    // El drag/persistencia/reset vienen de draggableColumnsMixin.
    cellStyle(key) {
      if (['company', 'area', 'position'].includes(key)) {
        return 'max-width: 200px; white-space: normal; word-break: break-word;'
      }
      if (['docs', 'options'].includes(key)) return 'text-align: center;'
      return ''
    },
    // Label del badge de DM activo. ENFERMEDAD/ACCIDENTE_* → descanso médico; MATERNIDAD aparte.
    medicalLeaveLabel(type) {
      return type === 'MATERNIDAD' ? 'EN MATERNIDAD' : 'EN DESCANSO MÉDICO'
    },
    // Label del badge de licencia activa según el code del tipo legal.
    leaveLabel(code) {
      const map = {
        PATERNIDAD: 'EN PATERNIDAD',
        FALLECIMIENTO: 'EN LICENCIA POR FALLECIMIENTO',
        ENFERMEDAD_GRAVE_FAMILIAR: 'EN LICENCIA POR ENFERMEDAD GRAVE',
      }
      return map[code] || 'EN LICENCIA'
    },
    // Espeja la regla del backend: un contrato POR INICIAR (futuro) solo es cesable si es la renovación
    // de otro contrato (c.isRenewal). Cualquier otro estado (vigente/por vencer) es cesable.
    canCeaseContract(c) {
      if (c && c.displayStatus === 'POR_INICIAR') return c.isRenewal === true
      return true
    },
    employeeActions(o) {
      const actions = [
        { key: 'view', label: 'Ver detalle' },
        { key: 'edit', label: 'Editar' },
      ]
      // ACTIVO y VENCIDO conservan "Dar de baja": el colaborador tiene contrato no cesado y RRHH
      // puede cesarlo formalmente (para VENCIDO es una de las dos acciones esperadas: renovar o cesar).
      // Se oculta solo si TODOS sus contratos vigentes son POR INICIAR no-renovación (no cesables).
      // VENCIDO no trae contratos vigentes en la lista → se conserva su comportamiento actual.
      const contracts = o.contracts || []
      const allNotCeaseable = contracts.length > 0 && !contracts.some(c => this.canCeaseContract(c))
      if (['ACTIVO', 'VENCIDO'].includes(o.displayStatus) && !allNotCeaseable) {
        actions.push({ key: 'baja', label: 'Dar de baja', danger: true })
      }
      return actions
    },
    handleEmployeeAction(employee, item) {
      if (item.key === 'view') this.$router.push({ name: 'employee-details', params: { id: employee.id } })
      if (item.key === 'edit') this.$router.push({ name: 'employee-edit',    params: { id: employee.id } })
      if (item.key === 'baja') this.startBaja(employee)
    },
    // Dar de baja = cesar el contrato activo de la empresa del contexto.
    startBaja(o) {
      const all = o.contracts || []
      if (!all.length) {
        Alert.error('El colaborador no tiene contratos activos')
        return
      }
      // Excluir POR INICIAR no-renovación: un contrato futuro sin origen aún no ha iniciado y no se cesa.
      const contracts = all.filter(c => this.canCeaseContract(c))
      if (!contracts.length) {
        Alert.error('No hay contratos que se puedan cesar: los contratos futuros solo se cesan si son una renovación')
        return
      }
      this.bajaEmployee = o
      if (contracts.length === 1) {
        this.openCeaseFor(o, contracts[0])
      } else {
        this.bajaContracts = contracts
        this.selectedBajaContractId = null
        this.companySelectModal = true
      }
    },
    openCeaseFor(o, contract) {
      this.ceaseTarget = { id: contract.id, number: contract.contractNumber, fullName: o.fullName }
      this.ceaseModal = true
    },
    confirmCompanySelect() {
      const contract = (this.bajaContracts || []).find(c => c.id === this.selectedBajaContractId)
      if (!contract) { Alert.error('Selecciona la empresa'); return }
      this.companySelectModal = false
      this.openCeaseFor(this.bajaEmployee, contract)
    },
    onCeased() {
      this.loadData()
    },
    onPaginationChange({ page, perPage }) {
      this.page = page
      this.pageSize = perPage
      this.loadData()
    },
    // Aplana los filtros de rango DATE al shape que espera el backend (@RequestBody FilterEmployeeRequest):
    // campos planos ISO contractInitiationStart/End y contractFinishStart/End. WzFilterMenu guarda cada
    // rango como objeto { start, end } bajo la clave del filtro; ese objeto lo descarta Jackson
    // (@JsonIgnoreProperties(ignoreUnknown=true)), por lo que sin aplanar el filtro nunca llega al backend.
    buildFilterPayload() {
      const data = { search: this.search, ...this.filters }
      const flattenRange = (key, fromField, toField) => {
        const range = this.filters[key]
        delete data[key]
        if (range && range.start) data[fromField] = range.start
        if (range && range.end)   data[toField]   = range.end
      }
      flattenRange('contractInitiation', 'contractInitiationStart', 'contractInitiationEnd')
      flattenRange('contractFinish',     'contractFinishStart',     'contractFinishEnd')
      return data
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const params = { page: this.page - 1, size: this.pageSize }
        const data = this.buildFilterPayload()
        const response = await EmployeeService.list(data, params)
        this.list = response.payload.content.map(item => ({
          ...item,
          _companyDisplay:  (item.contracts || []).map(c => c.companyName).filter(Boolean).join(', ') || '',
          _areaDisplay:     (item.contracts || []).map(c => c.areaName).filter(Boolean).join(', ') || '',
          _positionDisplay: (item.contracts || []).map(c => c.positionName).filter(Boolean).join(', ') || '',
        }))
        this.total = response.payload.totalElements
        this.pages = response.payload.totalPages
        this.loadPhotos()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors.message)
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    // Carga los thumbnails autenticados (bytes → objectURL), solo para los que tienen foto.
    async loadPhotos() {
      Object.values(this.photoUrls).forEach(u => URL.revokeObjectURL(u))
      this.photoUrls = {}
      await Promise.all(
        this.list.filter(o => o.hasPhoto).map(async (o) => {
          try {
            const blob = await EmployeeService.getPhoto(o.id)
            this.$set(this.photoUrls, o.id, URL.createObjectURL(blob))
          } catch (e) { /* sin foto */ }
        })
      )
    },
    statusVariant(displayStatus) {
      const map = {
        'ACTIVO':       'success-solid',
        'VENCIDO':      'warning-solid',
        'CESADO':       'dark',
        'SIN CONTRATO': 'secondary-solid',
      }
      return map[displayStatus] || 'primary-solid'
    },
    companies(o) {
      const list = Array.isArray(o.contracts) ? o.contracts : []
      const names = list.map(c => c.companyName).filter(Boolean)
      return names.length ? names.join(', ') : '-'
    },
    areas(o) {
      const list = Array.isArray(o.contracts) ? o.contracts : []
      const names = list.map(c => c.areaName).filter(Boolean)
      return names.length ? names.join(', ') : '-'
    },
    positions(o) {
      const list = Array.isArray(o.contracts) ? o.contracts : []
      const names = list.map(c => c.positionName).filter(Boolean)
      return names.length ? names.join(', ') : '-'
    },
    async getAreas() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = (await AreaService.listAll()).payload
        let optionItem = this.filterOptions.find(item => item.key === 'areaIds')
        optionItem.options = resp.map(item => ({ key: item.id, label: `${item.description}` }))
      } catch (ex) {
        Alert.error(ex.errors.message)
        console.error(ex)
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async getRoles() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = (await PositionService.listAll()).payload
        let optionItem = this.filterOptions.find(item => item.key === 'roleIds')
        optionItem.options = resp.map(item => ({ key: `${item.id}`, label: `${item.description}` }))
      } catch (ex) {
        Alert.error(ex.errors.message)
        console.error(ex)
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async getBusinesses() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = (await CompanyService.listAll()).payload
        let optionItem = this.filterOptions.find(item => item.key === 'companyId')
        optionItem.options = resp.map(item => ({ key: item.id, label: `${item.name}` }))
      } catch (ex) {
        Alert.error(ex.errors.message)
        console.error(ex)
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadReport() {
      try {
        await this.$store.dispatch('app/loading', true)
        const data = this.buildFilterPayload()
        const response = await EmployeeService.download(data)
        fileDownload(response.data, Util.getFileName(response))
      } catch (error) {
        console.error(error)
        Alert.error(await downloadErrorMessage(error) || 'Error al descargar el reporte de colaboradores')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    this.initColumns(defaultColumns(), this.columnsStorageKey, this.columnsListKey)
    // Filtros provenientes de un drill-down del dashboard (p. ej. "colaboradores sin contrato" →
    // status=SIN_CONTRATO). Sin esto el filtro quedaría en el default ACTIVO. companyId es SINGLE acá.
    const qStatus = this.$route.query.status
    const qCompanyId = this.$route.query.companyId
    if (qStatus) this.$set(this.filters, 'status', qStatus)
    if (qCompanyId) this.$set(this.filters, 'companyId', Number(qCompanyId))
    const hasUrlFilters = Object.keys(this.$route.query || {}).length > 0
    if (!hasUrlFilters && Object.keys(this.filters).length > 0) {
      const query = {}
      Object.entries(this.filters).forEach(([key, val]) => {
        if (val !== null && val !== '' && val !== undefined) query[key] = val
      })
      this.$router.replace({ query }).catch(() => {})
    }
    await this.loadData(this.page, this.pageSize)
    await this.getAreas()
    await this.getRoles()
    await this.getBusinesses()
  },
  beforeDestroy() {
    Object.values(this.photoUrls).forEach(u => URL.revokeObjectURL(u))
  },
}
</script>

<style lang="stylus">
.col-draggable
  cursor move
</style>
